import { Entity } from '@backstage/catalog-model';
import { z } from 'zod';
import {
  RBAC_API_VERSION,
  RBAC_POLICY_KIND,
  RBAC_ROLE_BINDING_KIND,
  RBAC_ROLE_KIND,
  RbacPolicyEntity,
  RbacRoleBindingEntity,
  RbacRoleEntity,
} from './types';
import { getEntityNamespace, parseRequiredEntityRef } from './refs';

const statementSchema = z.object({
  effect: z.enum(['allow', 'deny']),
  actions: z.array(z.string().min(1)).min(1),
  resources: z.array(z.string().min(1)).min(1),
});

const metadataSchema = z.object({
  name: z.string().min(1),
  namespace: z.string().min(1),
});

const baseEntitySchema = z.object({
  apiVersion: z.literal(RBAC_API_VERSION),
  metadata: metadataSchema,
});

export const rbacPolicySchema = baseEntitySchema.extend({
  kind: z.literal(RBAC_POLICY_KIND),
  spec: z.object({
    statements: z.array(statementSchema).min(1),
  }),
});

export const rbacRoleSchema = baseEntitySchema.extend({
  kind: z.literal(RBAC_ROLE_KIND),
  spec: z.object({
    description: z.string().optional(),
    policyRefs: z.array(z.string().min(1)).min(1),
  }),
});

export const rbacRoleBindingSchema = baseEntitySchema.extend({
  kind: z.literal(RBAC_ROLE_BINDING_KIND),
  spec: z.object({
    roleRef: z.string().min(1),
    subjects: z
      .array(
        z.object({
          kind: z.enum(['User', 'Group']),
          ref: z.string().min(1),
        }),
      )
      .min(1),
  }),
});

function formatZodError(error: z.ZodError): string {
  return error.issues
    .map(issue => `${issue.path.join('.') || 'entity'}: ${issue.message}`)
    .join('; ');
}

export function isRbacPolicyEntity(entity: Entity): entity is RbacPolicyEntity {
  return (
    entity.apiVersion === RBAC_API_VERSION && entity.kind === RBAC_POLICY_KIND
  );
}

export function isRbacRoleEntity(entity: Entity): entity is RbacRoleEntity {
  return (
    entity.apiVersion === RBAC_API_VERSION && entity.kind === RBAC_ROLE_KIND
  );
}

export function isRbacRoleBindingEntity(
  entity: Entity,
): entity is RbacRoleBindingEntity {
  return (
    entity.apiVersion === RBAC_API_VERSION &&
    entity.kind === RBAC_ROLE_BINDING_KIND
  );
}

export function isRbacEntity(entity: Entity): boolean {
  return (
    isRbacPolicyEntity(entity) ||
    isRbacRoleEntity(entity) ||
    isRbacRoleBindingEntity(entity)
  );
}

export function validateRbacPolicyEntity(entity: Entity): RbacPolicyEntity {
  const parsed = rbacPolicySchema.safeParse(entity);
  if (!parsed.success) {
    throw new Error(`Invalid RbacPolicy: ${formatZodError(parsed.error)}`);
  }
  return entity as RbacPolicyEntity;
}

export function validateRbacRoleEntity(entity: Entity): RbacRoleEntity {
  const parsed = rbacRoleSchema.safeParse(entity);
  if (!parsed.success) {
    throw new Error(`Invalid RbacRole: ${formatZodError(parsed.error)}`);
  }

  const namespace = getEntityNamespace(entity);
  for (const policyRef of parsed.data.spec.policyRefs) {
    const parsedRef = parseRequiredEntityRef(policyRef, {
      defaultKind: RBAC_POLICY_KIND,
    });
    if (parsedRef.namespace !== namespace) {
      throw new Error(
        `RbacRole policyRef "${policyRef}" must be in namespace "${namespace}"`,
      );
    }
  }

  return entity as RbacRoleEntity;
}

export function validateRbacRoleBindingEntity(
  entity: Entity,
): RbacRoleBindingEntity {
  const parsed = rbacRoleBindingSchema.safeParse(entity);
  if (!parsed.success) {
    throw new Error(`Invalid RbacRoleBinding: ${formatZodError(parsed.error)}`);
  }

  const namespace = getEntityNamespace(entity);
  const roleRef = parseRequiredEntityRef(parsed.data.spec.roleRef, {
    defaultKind: RBAC_ROLE_KIND,
  });
  if (roleRef.namespace !== namespace) {
    throw new Error(
      `RbacRoleBinding roleRef "${parsed.data.spec.roleRef}" must be in namespace "${namespace}"`,
    );
  }

  for (const subject of parsed.data.spec.subjects) {
    const subjectRef = parseRequiredEntityRef(subject.ref, {
      defaultKind: subject.kind,
    });
    if (!subjectRef.namespace) {
      throw new Error(
        `RbacRoleBinding subject "${subject.ref}" must include an explicit namespace`,
      );
    }
  }

  return entity as RbacRoleBindingEntity;
}

export function validateRbacEntity(entity: Entity): boolean {
  if (isRbacPolicyEntity(entity)) {
    validateRbacPolicyEntity(entity);
    return true;
  }
  if (isRbacRoleEntity(entity)) {
    validateRbacRoleEntity(entity);
    return true;
  }
  if (isRbacRoleBindingEntity(entity)) {
    validateRbacRoleBindingEntity(entity);
    return true;
  }
  return false;
}
